import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, ExternalLink, GraduationCap, Loader2, Send } from "lucide-react";
import { api } from "../../lib/api";
import { PHONE_MATCH, STAGE } from "../../lib/leadEnrollment";
import { glossFor } from "../../lib/paymentReason";
import GroupSessionsList from "../GroupSessionsList";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("ro-RO") : "");
const fmtMoney = (p) => (p?.amount != null ? `${Number(p.amount).toLocaleString("ro-RO")} ${p.currency || "RON"}` : "");

const MINISTRY = {
  completed: { label: "completat", cls: "text-[#2f6b50]" },
  requested: { label: "cerut, necompletat", cls: "text-[#8a6a1a]" },
  refused: { label: "refuzat", cls: "text-[#9b1f2c]" },
  missing: { label: "necerut încă", cls: "text-[#6B7280]" },
};
const CONTRACT = {
  completed: "semnat", participant_signed: "semnat de participant", sent: "trimis, nesemnat",
  rejected: "refuzat", canceled: "anulat", send_failed: "netrimis", sending: "se trimite",
};

/**
 * The lead's person among the enrollments, at the top of the lead drawer — so
 * whoever phones knows before dialling whether they are selling a course,
 * helping somebody finish paying, or talking to someone who already paid.
 *
 * Rows the caller may not see in full (a partner, somebody else's enrollment)
 * carry the stage alone; the dossier, contract and Logiscool lines arrive
 * only for owner and admin.
 */
export default function LeadEnrollmentsCard({ leadId, canMutate, onOpenEnrollment }) {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/admin/leads/${leadId}/enrollments`);
      setItems(r.data.items || []);
    } catch {
      setItems([]);
    }
  }, [leadId]);

  useEffect(() => { setItems(null); load(); }, [load]);

  const sendLink = async (item) => {
    const reminder = item.payment?.active_link;
    const q = reminder
      ? `Trimiți o reamintire de plată la ${item.email}, cu linkul existent?`
      : `Trimiți un link de plată la ${item.email}?`;
    if (!window.confirm(q)) return;
    setBusy(item.id);
    try {
      await api.post(`/admin/enrollments/${item.id}/send-payment-link`, { mode: reminder ? "send_reminder" : "send_link" });
      toast.success(reminder ? "Reamintirea de plată a fost trimisă." : "Linkul de plată a fost trimis.");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Linkul de plată nu a putut fi trimis.");
    } finally {
      setBusy(null);
    }
  };

  if (items == null) {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-[#E5E7EB] p-3 text-sm text-[#6B7280]">
        <Loader2 size={14} className="animate-spin" /> Caut înscrierile persoanei…
      </div>
    );
  }

  const byPhone = items.length > 0 && items[0].matched_by === "phone";
  // Somebody who has paid once is not sent a link for an older, abandoned
  // attempt — that is how people end up paying twice.
  const alreadyPaid = items.some((i) => i.stage === "paid" || i.stage === "confirmed");

  return (
    <section className="rounded-xl border border-[#E5E7EB] bg-white" data-testid="lead-enrollments-card">
      <div className="flex items-center gap-2 border-b border-[#E5E7EB] px-4 py-2.5 text-sm font-semibold text-[#373A45]">
        <GraduationCap size={15} className="text-[#965EA7]" /> Înscrierile acestei persoane
        <span className="font-normal text-[#6B7280]">({items.length})</span>
      </div>

      {items.length === 0 && (
        <p className="px-4 py-3 text-sm text-[#6B7280]" data-testid="lead-enrollments-none">
          Nicio înscriere cu acest email sau telefon. Apel de vânzare obișnuit.
        </p>
      )}

      {byPhone && (
        <div className="mx-4 mt-3 flex items-start gap-2 rounded-lg bg-[#3F8EBA]/10 px-3 py-2 text-xs text-[#1f5d80]" data-testid="lead-enrollments-phone">
          <AlertTriangle size={13} className="mt-0.5 shrink-0" />
          <span><strong>{PHONE_MATCH.label}.</strong> Înscrierile de mai jos au același telefon, dar alt email. Verifică la telefon înainte să vorbești despre ele.</span>
        </div>
      )}

      <ul className="divide-y divide-[#E5E7EB]">
        {items.map((it) => {
          const stage = STAGE[it.stage] || { label: it.stage, cls: "bg-[#EDEEF0] text-[#373A45]" };
          const p = it.payment;
          const reason = p?.failure_message ? (glossFor(p.failure_message) || p.failure_message) : "";
          const unpaid = ["pending", "abandoned"].includes(it.stage);
          return (
            <li key={it.id} className="px-4 py-3 text-sm" data-testid={`lead-enrollment-${it.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${stage.cls}`}>{stage.label}</span>
                {it.created_at && <span className="text-xs text-[#6B7280]">înscris pe {fmtDate(it.created_at)}</span>}
              </div>

              {/* Closed by the payment deadline (48 hours, 24 when seats run short): the reason this person
                  is on a call list at all (23.09.2026). */}
              {it.payment_expired_at && (
                <p className="mt-1.5 rounded-lg bg-[#FDF3E7] px-2.5 py-1.5 text-xs text-[#8a6a1a]"
                  data-testid={`lead-enrollment-expired-${it.id}`}>
                  Înscriere expirată pe {fmtDate(it.payment_expired_at)}: nu a fost plătită la termen, iar locul a fost eliberat.
                </p>
              )}

              {!it.can_open && (
                <p className="mt-1.5 text-xs text-[#6B7280]">Înscrierea nu îți este atribuită; vezi doar stadiul.</p>
              )}

              {it.can_open && (
                <div className="mt-2 grid gap-1 text-[13px] text-[#373A45]">
                  {it.email && it.matched_by === "phone" && <div>Email la înscriere: <strong>{it.email}</strong></div>}
                  <div>
                    Grupa: <strong>{it.group?.name || "neatribuită"}</strong>
                    {it.group?.schedule_label && <span className="text-[#6B7280]"> · {it.group.schedule_label}</span>}
                  </div>
                  <GroupSessionsList sessions={it.group?.sessions} testid={`lead-enrollment-sessions-${it.id}`} />
                  {p && (
                    <div>
                      Plată:{" "}
                      {p.status === "paid" && <strong className="text-[#2f6b50]">{fmtMoney(p)} · plătit {fmtDate(p.paid_at)}</strong>}
                      {p.status === "pending" && <span>link trimis pe {fmtDate(p.created_at)}, neplătit ({fmtMoney(p)})</span>}
                      {["failed", "cancelled"].includes(p.status) && (
                        <span className="text-[#9b1f2c]">
                          {p.status === "failed" ? "eșuată" : "anulată"} pe {fmtDate(p.created_at)}{reason ? ` — ${reason}` : ""}
                        </span>
                      )}
                      {!["paid", "pending", "failed", "cancelled"].includes(p.status) && <span>{p.status}</span>}
                    </div>
                  )}
                  {!p && unpaid && <div className="text-[#6B7280]">Plată: niciun link de plată trimis.</div>}
                  {it.ministry && (
                    <div>{it.regime === "fped" ? "Date pentru contract" : "Dosar MEC"}: <span className={MINISTRY[it.ministry.state]?.cls}>{MINISTRY[it.ministry.state]?.label}</span></div>
                  )}
                  {it.ministry && (
                    <div>
                      Contract: <span className={it.contract?.status === "completed" ? "text-[#2f6b50]" : "text-[#6B7280]"}>
                        {it.contract ? `${CONTRACT[it.contract.status] || it.contract.status} (${it.contract.number})` : "netrimis"}
                      </span>
                    </div>
                  )}
                  {it.limo && (
                    <div>Logiscool: {it.limo.enrolled
                      ? <span className="text-[#2f6b50]">înscris{it.limo.access_sent_at ? `, acces trimis pe ${fmtDate(it.limo.access_sent_at)}` : ", acces netrimis"}</span>
                      : <span className="text-[#6B7280]">neînscris</span>}</div>
                  )}
                </div>
              )}

              {it.can_open && (
                <div className="mt-2.5 flex flex-wrap gap-2">
                  {onOpenEnrollment && (
                    <button type="button" onClick={() => onOpenEnrollment(it.id)}
                      className="inline-flex items-center gap-1.5 rounded-full border border-[#965EA7] px-3 py-1 text-xs font-semibold text-[#965EA7] hover:bg-[#F0E7F4]"
                      data-testid={`lead-enrollment-open-${it.id}`}>
                      <ExternalLink size={12} /> Deschide înscrierea
                    </button>
                  )}
                  {canMutate && unpaid && alreadyPaid && (
                    <span className="text-xs text-[#6B7280]" data-testid={`lead-enrollment-paid-elsewhere-${it.id}`}>
                      Persoana are deja o înscriere plătită; nu trimite alt link.
                    </span>
                  )}
                  {canMutate && unpaid && !alreadyPaid && (
                    <button type="button" onClick={() => sendLink(it)} disabled={busy === it.id}
                      className="inline-flex items-center gap-1.5 rounded-full bg-[#965EA7] px-3 py-1 text-xs font-semibold text-white hover:bg-[#7A4986] disabled:opacity-50"
                      data-testid={`lead-enrollment-paylink-${it.id}`}>
                      {busy === it.id ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                      {p?.active_link ? "Trimite reamintire de plată" : "Trimite link de plată"}
                    </button>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
